import { Helmet } from '@dr.pogodin/react-helmet';
import type { FC } from 'react';

import { isProductionEnvironment } from '../../../../../utility/environments';
import useDocumentHeadIsPreparedForReactHelmet from '../../../hooks/useDocumentHeadIsPreparedForReactHelmet';
import { getCanonical, getNextPage, getPrevPage } from '../../../utils/metaUtils';
import type { PageHeadData, PageHeadPagination } from './PageHead.types';

interface PageHeadProps {
    data: PageHeadData;
    pagination?: PageHeadPagination;
}

const PageHead: FC<React.PropsWithChildren<PageHeadProps>> = (props) => {
    const link = { ...props.data.link };
    const meta = { ...props.data.meta };

    if (link && link.canonical) {
        link.canonical = getCanonical({
            path: link.canonical,
            page: props.pagination?.currentPage,
        });
    }

    let hasPrevPage = false;
    let hasNextPage = false;

    if (props.pagination) {
        hasPrevPage = props.pagination.currentPage !== 1;
        if (props.pagination.totalItems) {
            hasNextPage =
                props.pagination.pageSize * props.pagination.currentPage <=
                props.pagination.totalItems;
        }
    }

    if (hasPrevPage && link && props.pagination) {
        link.prevPage = getPrevPage(props.pagination.currentPage);
    }

    if (hasNextPage && link && props.pagination) {
        link.nextPage = getNextPage(props.pagination.currentPage);
    }

    const linkData =
        link &&
        Object.entries(link).filter(
            (item) => item[1] !== '' && item[1] !== undefined && item[1] !== null,
        );

    const metaData =
        meta &&
        Object.entries(meta).filter(
            (item) => item[1] !== '' && item[1] !== undefined && item[1] !== null,
        );

    let options: Parameters<typeof useDocumentHeadIsPreparedForReactHelmet>[0] = {
        link: linkData?.map(([key]) => ({ property: 'rel', value: key })),
    };

    if (metaData) {
        options = {
            ...options,
            meta: metaData.map(([key]) => ({ property: 'name', value: key })),
        };
    }

    const isDocumentHeadReadyForReactHelmet = useDocumentHeadIsPreparedForReactHelmet(options);

    return (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
            {isDocumentHeadReadyForReactHelmet && (
                <Helmet>
                    {props.data.title && (
                        <title>
                            {props.data.title +
                                (!isProductionEnvironment ? ' - NOT FOR COMMERCIAL USE' : '')}
                        </title>
                    )}
                    {metaData?.map(([property, value]) => (
                        <meta key={property} name={property} content={value} />
                    ))}

                    {linkData?.map(([property, value]) => (
                        <link key={property} rel={property} href={value} />
                    ))}

                    {props.children}
                </Helmet>
            )}
        </>
    );
};

export default PageHead;
